import {
  CalendarMeetingTemplateStatus,
  SearchCalendarMeetingsQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { CalendarMeetingTemplateFormData } from '../constants/calendarMeetingTemplateSchema';
import { UseUserCalendarReturnData } from '../hooks/useUserCalendar';
import { convertTimeUnitValuesToMinutes } from './convertTimeUnitValuesToMinutes';
import { ApolloListItemType } from 'core/types/ApolloListQuery';
import { MeetingLocation } from '../constants/calendarMeetingTemplateSchema';

export const mapFormDataToMeetingTemplate = ({
  formData,
  id,
  userCalendars
}: {
  formData: CalendarMeetingTemplateFormData;
  id: string;
  userCalendars: UseUserCalendarReturnData;
}): ApolloListItemType<SearchCalendarMeetingsQuery> => {
  const organizerUserCalendar = userCalendars.find(
    (userCalendar) => userCalendar.id === formData.owner
  );

  // The organizer is one of the panel participants server-side, and the list
  // filters them back out by id.
  const panelIds = [
    ...(organizerUserCalendar ? [organizerUserCalendar.id] : []),
    ...(formData.participants ?? []).filter(
      (participantId): participantId is string =>
        !!participantId && participantId !== organizerUserCalendar?.id
    )
  ];

  // Apollo reads the cache through the list query document: one field short of
  // it and the whole entry is incomplete, so the row is silently dropped.
  return {
    id,
    title: formData.title,
    durationMinutes: convertTimeUnitValuesToMinutes(
      formData.durationValue,
      formData.durationUnit
    ),
    createdAt: new Date().toISOString(),
    totalBookingsCount: null,
    availabilityMethod: formData.schedulingMethod,
    organizerUserCalendarId: organizerUserCalendar?.id || '',
    updatedAt: '',
    status: CalendarMeetingTemplateStatus.Active,
    updatedByUserId: '',
    location:
      formData.location === MeetingLocation.InPerson
        ? formData.locationDetails?.trim() ?? null
        : null,
    isVideoConference: formData.location === MeetingLocation.Video,
    participants: {
      items: panelIds.map((participantId) => {
        const userCalendar = userCalendars.find(
          (calendar) => calendar.id === participantId
        );

        return {
          userCalendar: {
            id: participantId,
            name: userCalendar?.name ?? ''
          }
        };
      })
    },
    organizerUserCalendar: organizerUserCalendar
      ? {
          id: organizerUserCalendar.id,
          name: organizerUserCalendar.name,
          reference: organizerUserCalendar.reference,
          status: organizerUserCalendar.status,
          userCalendarOrganizations: {
            items: [{ id: organizerUserCalendar.userCalendarOrganizationId }]
          }
        }
      : null
  };
};
